import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, PlayCircle } from 'lucide-react';

const Hero: React.FC = () => {
    return (
        <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-navy-900">
            {/* Background Effects */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-brand-blue/20 rounded-full blur-[120px] opacity-30 pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[800px] h-[600px] bg-brand-cyan/10 rounded-full blur-[100px] opacity-20 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.05] text-brand-cyan text-sm font-medium mb-6">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-cyan opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-cyan"></span>
                        </span>
                        Accepting new clients for Q1 2026
                    </div>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-tight mb-6"
                >
                    Automate Your Business. <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-brand-blue">
                        Work Smarter, Not Harder.
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="text-lg md:text-xl text-text-muted max-w-2xl mx-auto mb-10 leading-relaxed"
                >
                    We build AI-powered workflows and automation systems that eliminate repetitive work, save time, and improve efficiency for ambitious businesses.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href="#contact"
                        className="group relative px-8 py-4 rounded-xl bg-gradient-to-r from-brand-orange to-brand-coral text-white font-bold shadow-lg shadow-brand-orange/25 hover:shadow-brand-orange/40 transition-all hover:-translate-y-1 overflow-hidden"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            Book a Free Consultation
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                    </a>

                    <a
                        href="#work"
                        className="px-8 py-4 rounded-xl bg-white/[0.05] border border-white/[0.1] text-white font-semibold hover:bg-white/[0.1] transition-all flex items-center gap-2"
                    >
                        <PlayCircle className="w-5 h-5 text-brand-cyan" />
                        See Automations We've Built
                    </a>
                </motion.div>

                {/* Tech Stack Marquee Placeholder */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.8 }}
                    className="mt-20 pt-10 border-t border-white/[0.05]"
                >
                    <p className="text-sm text-text-muted mb-6 uppercase tracking-wider font-semibold">Powering businesses with</p>
                    <div className="flex justify-center gap-8 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                        {/* Simple text placeholders for logos for now */}
                        <span className="text-xl font-bold text-white">n8n</span>
                        <span className="text-xl font-bold text-white">OpenAI</span>
                        <span className="text-xl font-bold text-white">Zapier</span>
                        <span className="text-xl font-bold text-white">Make</span>
                        <span className="text-xl font-bold text-white">Supabase</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
